import dynamic from "next/dynamic";
import OptimizedLink from "../../Atoms/OptimizedLink";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { PiShoppingCart, RxCaretDown } from "../../Atoms/AppIcons";
import { useCachedGql } from "../../../hooks/useCachedGql";
import {
  CART_ITEMS_STORAGE_KEY,
  CART_UPDATED_EVENT,
  getCartItemCount,
} from "../../../utils/cart/cartStorage";
import { EventTracker } from "../../Atoms/Analytics";
import CurrencyPicker from "../../Atoms/CurrencyPicker";
import { IconLink } from "../../Atoms/IconButton";
import SelectButton from "../../Atoms/SelectButton";
import SearchBar from "../../Cards/SearchBar";
import CheckoutHeader from "../CheckoutHeader";
import Logo from "../Logo";
import { CountryPicker } from "./CountryPicker";
import styles from "./Header.module.scss";
import MyTripsButton from "./MyTripsButton";

// Loaded on first open so react-modal stays out of the shared layout bundle.
const MobileDestinationModal = dynamic(
  () => import("./MobileDestinationModal"),
  {
    ssr: false,
  },
);

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  if (isOpen && !hasOpened) setHasOpened(true);
  const [cartItemCount, setCartItemCount] = useState(0);
  const router = useRouter();
  const showCurrency = router.pathname !== "/bookings/[id]";

  const isCheckout =
    router.pathname === "/bookings/[id]" ||
    router.pathname === "/checkout/[id]" ||
    router.pathname === "/payments/[id]"
      ? true
      : false;

  const hideHeader =
    isCheckout ||
    router.pathname === "/guide/[guideId]/sign-contracts" ||
    router.pathname === "/guide/sign-contracts-success" ||
    router.pathname === "/bookings/[id]/chat" ||
    router.pathname === "/performance-test" ||
    (router.asPath.startsWith("/performance-test") &&
      router.asPath !== "/performance-test/header");

  const { data, isLoading: loading } = useCachedGql(
    "getCitiesAndPrefecturesWithProducts",
    { skip: hideHeader, mode: "server" },
  );
  const locations = useMemo(() => data?.locations.records ?? [], [data]);

  useEffect(() => {
    const updateCartItemCount = () => setCartItemCount(getCartItemCount());
    const handleStorage = (event: StorageEvent) => {
      if (event.key === CART_ITEMS_STORAGE_KEY) updateCartItemCount();
    };

    updateCartItemCount();
    window.addEventListener(CART_UPDATED_EVENT, updateCartItemCount);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, updateCartItemCount);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      setIsOpen(false); // Close the modal on route change
    };

    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {isCheckout ? <CheckoutHeader /> : null}
      {!hideHeader && (
        <div
          id="AppHeader"
          className={styles.AppHeader}
          style={hideHeader ? { display: "none" } : null}
        >
          <div className="AppLayout" style={{ overflow: "inherit" }}>
            <div className={styles.AppHeaderInner}>
              <OptimizedLink
                href="/?ref=logo"
                title="MagicalTrip Homepage"
                className={styles.AppHeaderLogo}
                prefetchMode="disable"
              >
                <Logo height={40} color="#fff" />
              </OptimizedLink>

              <CountryPicker locations={locations} loading={loading} />
              <EventTracker
                onClick={{
                  category: "header",
                  action: "click",
                  label: "destination-picker",
                }}
              >
                <SelectButton
                  pressed={isOpen}
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <p>Discover</p>
                  <RxCaretDown size={24} style={{ margin: "0px -6px" }} />
                </SelectButton>
              </EventTracker>
              {hasOpened && (
                <MobileDestinationModal
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  locations={locations}
                  loading={loading}
                />
              )}
              <div className={styles.ButtonGroup}>
                {cartItemCount > 0 && (
                  <span className={styles.CartButton}>
                    <IconLink
                      href="/cart"
                      aria-label={`View shopping cart, ${cartItemCount} ${
                        cartItemCount === 1 ? "item" : "items"
                      }`}
                      title="Shopping cart"
                      prefetch={false}
                    >
                      <PiShoppingCart size={24} />
                    </IconLink>
                    <span className={styles.CartBadge} aria-hidden="true">
                      {cartItemCount > 9 ? "9+" : cartItemCount}
                    </span>
                  </span>
                )}
                <SearchBar variant="tiny" />
                <MyTripsButton />

                <nav
                  className={styles.AppHeaderNav}
                  style={{ alignItems: "flex-end" }}
                >
                  {showCurrency ? (
                    <ul>
                      <li>
                        <CurrencyPicker />
                      </li>
                    </ul>
                  ) : null}
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* <SiteWideMessage /> */}
    </>
  );
}
